import { inject } from "fw";
import { Store, handle, dispatch } from "fw-state";
import { debounce } from "lodash-es";

import { LogoutAction, StartAction } from "./actions";
import { CasOrganizationIntegrationClientModel } from "models/cas-organization-integration";
import { CasProgramIntegrationClientModel } from "models/cas-program-integration";
import { EntitySelection } from "network/manager";
import { CasOrganizationIntegrationRepository } from "network/cas-organization-integration-repository";
import { CasProgramIntegrationRepository } from "network/cas-program-integration-repository";
import { CurrentAtsOrgSummaryRefreshAction } from "state/current-ats-organization-summary";

const DEFAULT_PAGE_SIZE = 20;

type CurrentCasOrganizationStoreShape = {
    casOrganizationIntegrationId: string;
    casOrganizationIntegration: CasOrganizationIntegrationClientModel;
    casOrganizationIntegrationSaving: boolean;
    firstPageLoaded: boolean;
    loaded: boolean;
    errorLoading: boolean;
    search: string;
    filter: string;
    sort: string;
    programs: CasProgramIntegrationClientModel[];
    total: number;
    previousPageToken: string;
    nextPageToken: string;
    pageSize: number;
    pageNumber: number;
    selectAll: boolean;
    selection: EntitySelection;
};

export class CurrentCasOrgEnsureAction {
    constructor(public casOrganizationIntegrationId: string) {}
}
export class CurrentCasOrgRefreshAction {}
export class CurrentCasOrgClearAction {}
export class CurrentCasOrgSetSearchAction {
    constructor(public search: string) {}
}
export class CurrentCasOrgSetFilterAction {
    constructor(public filter: string) {}
}
export class CurrentCasOrgToggleSortAction {
    constructor(public sort: string) {}
}
export class CurrentCasOrgNextPageAction {}
export class CurrentCasOrgPreviousPageAction {}
export class CurrentCasOrgAddToSelectionAction {
    constructor(public ids: string[] = []) {}
}
export class CurrentCasOrgRemoveFromSelectionAction {
    constructor(public ids: string[] = []) {}
}
export class CurrentCasOrgDeleteSelectionAction {}

export class CurrentCasOrgAutoProvisionProgramsAction {
    constructor(
        public casOrgIntegrationId: string,
        public value: boolean = false,
        public programTemplateId: string = null
    ) {}
}

export class CurrentCasOrgEnabledAction {
    constructor(
        public casOrgIntegrationId: string,
        public value: boolean = false
    ) {}
}

@inject
export class CurrentCasOrganizationStore extends Store<CurrentCasOrganizationStoreShape> {
    private debouncedLoadStoreData = async () => {};

    constructor(
        private casOrgIntegrationRepo: CasOrganizationIntegrationRepository,
        private casProgramIntegrationRepo: CasProgramIntegrationRepository
    ) {
        super();
        this.debouncedLoadStoreData = debounce(() => this.loadStoreData(), 250);
    }

    defaultState(): CurrentCasOrganizationStoreShape {
        return {
            casOrganizationIntegrationId: null,
            casOrganizationIntegration: null,
            casOrganizationIntegrationSaving: false,

            firstPageLoaded: false,
            loaded: false,
            errorLoading: false,
            search: null,
            filter: null,
            sort: null,
            programs: [],
            total: 0,
            previousPageToken: null,
            nextPageToken: null,
            pageSize: DEFAULT_PAGE_SIZE,
            pageNumber: 1,
            selectAll: false,
            selection: null
        };
    }

    @handle(StartAction)
    private handleStartAction(action: StartAction) {
        this.setState(() => this.defaultState());
    }
    @handle(LogoutAction)
    private handleLogoutAction(action: LogoutAction) {
        this.setState(() => this.defaultState());
    }
    @handle(CurrentCasOrgClearAction)
    private handleClearAction(action: CurrentCasOrgClearAction) {
        this.setState(() => ({
            ...this.defaultState(),
            loaded: true
        }));
    }

    @handle(CurrentCasOrgEnsureAction)
    private async handleEnsure(action: CurrentCasOrgEnsureAction): Promise<void> {
        const { casOrganizationIntegrationId, loaded } = this.state;

        try {
            this.setState((state) => ({
                ...state,
                loaded: false
            }));

            if (action.casOrganizationIntegrationId !== casOrganizationIntegrationId || !loaded) {
                const casOrganizationIntegration = await this.casOrgIntegrationRepo.getClientModel(action.casOrganizationIntegrationId);

                this.setState(() => ({
                    ...this.defaultState(),
                    firstPageLoaded: false,
                    errorLoading: false,
                    selectAll: false,
                    selection: null,
                    casOrganizationIntegrationId: action.casOrganizationIntegrationId,
                    casOrganizationIntegration
                }));
            }

            await this.loadStoreData();
        } catch {
            this.setState(() => ({
                ...this.defaultState(),
                loaded: true,
                errorLoading: true
            }));
        }
    }

    @handle(CurrentCasOrgRefreshAction)
    private async handleRefresh(action: CurrentCasOrgRefreshAction) {
        if (!this.state.loaded) return;

        const casOrganizationIntegration = await this.casOrgIntegrationRepo.getClientModel(this.state.casOrganizationIntegrationId);

        this.setState((state) => ({
            ...state,
            casOrganizationIntegration,
            errorLoading: false
        }));

        await this.loadStoreData();
    }

    @handle(CurrentCasOrgSetSearchAction)
    private async handleSetSearch(action: CurrentCasOrgSetSearchAction) {
        const { filter } = this.state;
        this.setState((state) => ({
            ...state,
            search: action.search,
            filter,
            errorLoading: false,
            sort: null,
            previousPageToken: null,
            nextPageToken: null,
            pageSize: DEFAULT_PAGE_SIZE,
            pageNumber: 1,
            selectAll: false,
            selection: null
        }));
        await this.debouncedLoadStoreData();
    }

    @handle(CurrentCasOrgSetFilterAction)
    private async handleSetFilter(action: CurrentCasOrgSetFilterAction) {
        const { search } = this.state;
        this.setState((state) => ({
            ...state,
            search,
            filter: action.filter,
            errorLoading: false,
            sort: null,
            previousPageToken: null,
            nextPageToken: null,
            pageSize: DEFAULT_PAGE_SIZE,
            pageNumber: 1,
            selectAll: false,
            selection: null
        }));
        await this.loadStoreData();
    }

    @handle(CurrentCasOrgNextPageAction)
    private async handleNextPage(action: CurrentCasOrgNextPageAction) {
        const { nextPageToken, pageSize, pageNumber, total } = this.state;

        if (pageNumber * pageSize > total) {
            return;
        }

        this.setState((state) => ({
            ...state,
            loaded: false,
            pageNumber: pageNumber + 1
        }));

        await this.loadPageData(null, nextPageToken);
    }

    @handle(CurrentCasOrgPreviousPageAction)
    private async handlePreviousPage(action: CurrentCasOrgPreviousPageAction) {
        const { previousPageToken, pageNumber } = this.state;
        if (pageNumber <= 1) {
            return;
        }

        this.setState((state) => ({
            ...state,
            loaded: false,
            pageNumber: pageNumber - 1
        }));

        await this.loadPageData(previousPageToken, null);
    }

    private async loadPageData(previousPageToken: string, nextPageToken: string) {
        const { casOrganizationIntegrationId, search, pageSize, sort } = this.state;
        const response = await this.casProgramIntegrationRepo.searchClientModels(
            casOrganizationIntegrationId,
            search,
            null,
            sort,
            pageSize,
            previousPageToken,
            nextPageToken
        );
        this.setState((state) => ({
            ...state,
            loaded: true,
            previousPageToken: response.previousPageToken,
            nextPageToken: response.nextPageToken,
            programs: response.list,
            total: response.total
        }));
    }

    @handle(CurrentCasOrgToggleSortAction)
    private async handleToggleSort(action: CurrentCasOrgToggleSortAction) {
        if (action.sort == this.state.sort) {
            this.setState((state) => ({ ...state, sort: `-(${action.sort})` }));
        } else {
            this.setState((state) => ({ ...state, sort: action.sort }));
        }
        await this.loadStoreData();
    }

    @handle(CurrentCasOrgAddToSelectionAction)
    private async handleAddToSelection(action: CurrentCasOrgAddToSelectionAction) {
        const { search, filter } = this.state;
        let { selectAll, selection } = this.state;

        selection = selection || new EntitySelection();

        if (action.ids.length === 0) {
            // select all.
            selectAll = true;
            selection.search = search;
            selection.filter = filter;
            selection.ids = [];
            selection.excludedIds = [];
        } else {
            if (selection.excludedIds.length > 0) {
                for (const id of action.ids) {
                    const index = selection.excludedIds.indexOf(id);
                    if (index > -1) {
                        selection.excludedIds.splice(index, 1);
                    }
                }
            }
            selection.ids = [...new Set([...selection.ids, ...action.ids])];
        }

        this.setState((state) => ({
            ...state,
            selectAll,
            selection
        }));
    }

    @handle(CurrentCasOrgRemoveFromSelectionAction)
    private async handleRemoveFromSelection(action: CurrentCasOrgRemoveFromSelectionAction) {
        let { selectAll, selection } = this.state;
        if (action.ids.length === 0) {
            // unselect all.
            selectAll = false;
            selection = null;
        } else {
            selection = selection || new EntitySelection();
            if (selection.ids.length > 0) {
                for (const id of action.ids) {
                    const index = selection.ids.indexOf(id);
                    if (index > -1) {
                        selection.ids.splice(index, 1);
                    }
                }
            }

            selection.excludedIds = [...new Set([...selection.excludedIds, ...action.ids])];
        }

        this.setState((state) => ({
            ...state,
            selectAll,
            selection
        }));
    }

    @handle(CurrentCasOrgDeleteSelectionAction)
    private async handleDeleteSelection(action: CurrentCasOrgDeleteSelectionAction) {
        const { casOrganizationIntegrationId, selectAll, selection } = this.state;
        await this.casProgramIntegrationRepo.deleteSelection(selection, casOrganizationIntegrationId, selectAll);

        this.setState((state) => ({
            ...state,
            errorLoading: false,
            sort: null,
            previousPageToken: null,
            nextPageToken: null,
            pageSize: DEFAULT_PAGE_SIZE,
            pageNumber: 1,
            selectAll: false,
            selection: null
        }));

        await this.loadStoreData();
        await dispatch(new CurrentAtsOrgSummaryRefreshAction());
    }

    @handle(CurrentCasOrgAutoProvisionProgramsAction)
    private async handleActiveProgramsIntegrationType(action: CurrentCasOrgAutoProvisionProgramsAction) {
        if (!this.state.loaded) return;

        // because this store generally works with the integration client model, we need to fetch a recent copy of the raw model
        // so we can patch in the new settings and PUT the whole thing
        const integration = await this.casOrgIntegrationRepo.get(action.casOrgIntegrationId);
        integration.AutoProvisionPrograms = action.value;
        integration.AutoProvisionProgramTemplateId = action.programTemplateId;

        const casOrganizationIntegration = await this.casOrgIntegrationRepo.put(action.casOrgIntegrationId, integration);

        this.setState((state) => ({
            ...state,
            casOrganizationIntegration: {
                ...state.casOrganizationIntegration,
                AutoProvisionPrograms: casOrganizationIntegration.AutoProvisionPrograms,
                AutoProvisionProgramTemplateId: casOrganizationIntegration.AutoProvisionProgramTemplateId
            }
        }));

        await this.loadStoreData();
        await dispatch(new CurrentAtsOrgSummaryRefreshAction());
    }

    @handle(CurrentCasOrgEnabledAction)
    private async handleEnabledIntegrationType(action: CurrentCasOrgEnabledAction) {
        const { loaded, casOrganizationIntegrationSaving } = this.state;
        if (!loaded || casOrganizationIntegrationSaving) {
            return;
        }

        try {
            this.setState((state) => ({
                ...state,
                casOrganizationIntegrationSaving: true
            }));

            // because this store generally works with the integration client model, we need to fetch a recent copy of the raw model
            // so we can patch in the new settings and PUT the whole thing
            const integration = await this.casOrgIntegrationRepo.get(action.casOrgIntegrationId);
            integration.Enabled = action.value;

            const casOrganizationIntegration = await this.casOrgIntegrationRepo.put(action.casOrgIntegrationId, integration);

            this.setState((state) => ({
                ...state,
                casOrganizationIntegration: {
                    ...state.casOrganizationIntegration,
                    Enabled: casOrganizationIntegration.Enabled
                }
            }));

            await this.loadStoreData();
            await dispatch(new CurrentAtsOrgSummaryRefreshAction());
        } finally {
            this.setState((state) => ({
                ...state,
                casOrganizationIntegrationSaving: false
            }));
        }
    }

    private async loadStoreData() {
        const { casOrganizationIntegrationId, pageSize, sort, search, filter } = this.state;

        // search doesn't index values until the length is greater than 3
        const activeSearch = search && search.length > 2 ? search : null;

        try {
            const response = await this.casProgramIntegrationRepo.searchClientModels(casOrganizationIntegrationId, activeSearch, filter, sort, pageSize);
            this.setState((state) => ({
                ...state,
                firstPageLoaded: true,
                loaded: true,
                errorLoading: false,
                programs: response.list,
                total: response.total,
                previousPageToken: response.previousPageToken,
                nextPageToken: response.nextPageToken
            }));
        } catch (err) {
            this.setState((state) => ({
                ...state,
                firstPageLoaded: true,
                loaded: true,
                errorLoading: true
            }));
        }
    }
}
